// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: lets one surface turn parts of rendered Markdown into its own elements without its own
// renderer: an inline code span or a run of plain text. Agent replies use it to make file paths
// tappable (konvo_agents AgentReply), give each table its own copy button, web links a hover card and
// shell blocks a Run in terminal header (AgentCodeRun). Returning null keeps Konvo's usual rendering; nothing is
// decorated unless a surface provides this.

import type React from 'react';
import {createContext} from 'react';

export interface MarkdownDecorations {
	/** An inline code span's text: an element to show instead, or null for the usual `<code>`. */
	code?: (text: string, key: string) => React.ReactNode | null;
	/** A run of plain text: an element to show instead, or null for the usual text. */
	text?: (text: string, key: string) => React.ReactNode | null;
	/** A rendered table: an element to show in its place (usually wrapping it), or null to leave it. */
	table?: (table: React.ReactElement, key: string) => React.ReactNode | null;
	/** A web link (http/https) as rendered: an element to show in its place (usually wrapping it), or null to leave it. */
	link?: (link: React.ReactElement, url: string, key: string) => React.ReactNode | null;
	/**
	 * A fenced code block with a language (highlighted): an element to show in its place, or null for
	 * the usual block. It gets the block's `<pre>` and copy button to build its own around them.
	 */
	codeBlock?: (block: MarkdownCodeBlock, key: string) => React.ReactNode | null;
}

export interface MarkdownCodeBlock {
	content: string;
	/** Lower case, the first word after the fence (`bash`, `sh`, `console`…). */
	language: string;
	/** The highlighted `<pre>`. */
	pre: React.ReactElement;
	/** The usual copy button (shown on hover in the block's corner). */
	copy: React.ReactElement;
}

export const MarkdownDecorationsContext = createContext<MarkdownDecorations | null>(null);
